import { createContext, useCallback, useContext, useEffect, useReducer, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';

import { sendSavedGoalsSettingsEvent } from 'constants/mixpanelHelper';

import { setObjectiveBehaviourSettings, setSelectedTemplate } from 'services/admin';
import { updateGoalRecurrenceDetails } from 'services/checkIn';
import { showErrorSnackbar, showSuccessSnackbar } from 'services/snackbar';

import { REACT_QUERY_GOALS_ACCESS_CONTROL } from '../constants';
import { useGoalFrequencyData } from '../hooks/dataHooks';

const initialGoalPreferencesState = {
  templateList: [],
  goalPreferenceDetails: {
    frequencyType: '',
    startMonth: '',
  },
  checkinReminder: {},
  isLoading: true,
  isGoalsFreqUpdated: false,
  isCheckinReminderUpdated: false,
  isTemplateConfigUpdated: false,
  modal: {
    isOpen: false,
    heading: '',
    text: '',
    onSuccess: () => {},
  },
};

const GoalPreferencesContext = createContext({});

export const goalPreferencesActions = {
  INITIALIZE_DATA: 'INITIALIZE_DATA',
  SET_CHECKIN_REMINDER_DETAILS: 'SET_CHECKIN_REMINDER_DETAILS',
  UPDATE_GOAL_FREQUENCY_DETAILS: 'UPDATE_GOAL_FREQUENCY_DETAILS',
  UPDATE_CHECKIN_REMINDER_DETAILS: 'UPDATE_CHECKIN_REMINDER_DETAILS',
  UPDATE_TEMPLATE_CONFIG: 'UPDATE_TEMPLATE_CONFIG',
  UPDATE_CONFIRMATION_MODAL: 'UPDATE_CONFIRMATION_MODAL',
  SAVE_CHANGES: 'SAVE_CHANGES',
  CLONE_TEMPLATE: 'CLONE_TEMPLATE',
  DELETE_TEMPLATE: 'DELETE_TEMPLATE',
  IS_GOAL_FREQUENCY_UPDATED: 'IS_GOAL_FREQUENCY_UPDATED',
};

const saveGoalPreferences = (data, templateData, goalPreferenceDetails) => {
  const resultArray = [];
  const saveGoalFrequencySettings = () =>
    setObjectiveBehaviourSettings(goalPreferenceDetails)
      .then((res) => {})
      .catch((err) => {});

  const saveGoalRecurrence = () =>
    updateGoalRecurrenceDetails(data.checkinReminder)
      .then((res) => {})
      .catch((err) => {});
  const saveTemplateConfig = () => {
    const tempId = templateData?.filter((temp) => temp.selected === true)[0].id;
    return setSelectedTemplate(tempId)
      .then((res) => {
        // To refresh the application with new template config by calling authenticate api on reload
        window.location.reload();
      })
      .catch((err) => {});
  };

  if (data.isGoalsFreqUpdated) {
    resultArray.push(saveGoalFrequencySettings);
  }
  if (data.isCheckinReminderUpdated) {
    resultArray.push(saveGoalRecurrence);
  }
  if (data.isTemplateConfigUpdated) {
    resultArray.push(saveTemplateConfig);
  }

  return Promise.allSettled(resultArray.map((res) => res()));
};

const goalPreferencesReducer = (state, action) => {
  const { type: actionType, payload } = action;
  switch (actionType) {
    case goalPreferencesActions.INITIALIZE_DATA: {
      return {
        ...state,
        templateList: payload.templateList,
        goalPreferenceDetails: payload.goalPreferenceDetails,
        isLoading: payload.isLoading,
      };
    }
    case goalPreferencesActions.UPDATE_GOAL_FREQUENCY_DETAILS: {
      return {
        ...state,
        goalPreferenceDetails: {
          ...state.goalPreferenceDetails,
          [payload.key]: payload.value,
        },
        isGoalsFreqUpdated: true,
      };
    }
    case goalPreferencesActions.SET_CHECKIN_REMINDER_DETAILS: {
      return {
        ...state,
        checkinReminder: payload.data,
      };
    }
    case goalPreferencesActions.UPDATE_CHECKIN_REMINDER_DETAILS: {
      return {
        ...state,
        isCheckinReminderUpdated: true,
      };
    }
    case goalPreferencesActions.UPDATE_TEMPLATE_CONFIG: {
      return {
        ...state,
        isTemplateConfigUpdated: true,
      };
    }
    case goalPreferencesActions.UPDATE_CONFIRMATION_MODAL: {
      return {
        ...state,
        modal: {
          heading: action.payload.heading,
          isOpen: action.payload.isOpen,
          text: action.payload.text,
          onSuccess: action.payload.onSuccess,
        },
      };
    }

    case goalPreferencesActions.SAVE_CHANGES: {
      return {
        ...state,
        isGoalsFreqUpdated: false,
        isCheckinReminderUpdated: false,
        isTemplateConfigUpdated: false,
      };
    }
    case goalPreferencesActions.CLONE_TEMPLATE: {
      return {
        ...state,
        templateList: [...state.templateList, payload.data],
      };
    }
    case goalPreferencesActions.DELETE_TEMPLATE: {
      return {
        ...state,
        templateList: state.templateList.filter((template) => template.id !== payload.id),
      };
    }

    case goalPreferencesActions.IS_GOAL_FREQUENCY_UPDATED: {
      return {
        ...state,
        isGoalsFreqUpdated: true,
      };
    }

    default:
      return state;
  }
};

const GoalPreferencesProvider = ({ children }) => {
  const [state, goalPreferenceDispatch] = useReducer(goalPreferencesReducer, initialGoalPreferencesState);

  const dispatch = useDispatch();

  const queryClient = useQueryClient();

  const templatesData = queryClient.getQueryData([REACT_QUERY_GOALS_ACCESS_CONTROL.allTemplates]);

  const { data, isLoading } = useGoalFrequencyData();

  // wrongly implemented initially so using this to get the data
  // don't even ask me why there's a context provider, react query and redux slice being used :((((((((((
  const [goalPreferenceDetails, setGoalPreferenceDetails] = useState();

  useEffect(() => {
    if (data) {
      setGoalPreferenceDetails(data);
    }
  }, [data]);

  const onGoalFrequencyChange = useCallback((key, value) => {
    setGoalPreferenceDetails((prevState) => ({
      ...prevState,
      [key]: value,
    }));
    // updating IS_GOAL_FREQUENCY_UPDATED to true
    goalPreferenceDispatch({
      type: goalPreferencesActions.IS_GOAL_FREQUENCY_UPDATED,
    });
  }, []);

  const saveChanges = () => {
    saveGoalPreferences(state, templatesData, goalPreferenceDetails)
      .then((res) => {
        goalPreferenceDispatch({
          type: goalPreferencesActions.SAVE_CHANGES,
        });
        goalPreferenceDispatch({
          type: goalPreferencesActions.UPDATE_CONFIRMATION_MODAL,
          payload: {
            isOpen: false,
            text: '',
            heading: '',
            onSuccess: () => {},
          },
        });
        queryClient.invalidateQueries([REACT_QUERY_GOALS_ACCESS_CONTROL.allTemplates]);
        queryClient.invalidateQueries([REACT_QUERY_GOALS_ACCESS_CONTROL.goalsConfigurations]);
        sendSavedGoalsSettingsEvent();
        dispatch(showSuccessSnackbar('Changes saved successfully.'));
      })
      .catch((err) => {
        dispatch(showErrorSnackbar(err.message || 'Something went wrong.'));
      });
  };

  const cancelChanges = () => {
    goalPreferenceDispatch({
      type: goalPreferencesActions.UPDATE_CONFIRMATION_MODAL,
      payload: {
        isOpen: false,
        text: '',
        heading: '',
        onSuccess: () => {},
      },
    });
    window.location.reload();
  };

  const changeTemplateConfig = () => {
    goalPreferenceDispatch({
      type: goalPreferencesActions.UPDATE_TEMPLATE_CONFIG,
    });
  };

  return (
    <GoalPreferencesContext.Provider
      value={{
        state,
        goalPreferenceDispatch,
        onGoalFrequencyChange,
        saveChanges,
        cancelChanges,
        goalPreferenceDetails,
        changeTemplateConfig,
      }}
    >
      {children}
    </GoalPreferencesContext.Provider>
  );
};

export default GoalPreferencesProvider;

export const useGoalPreferencesContext = () => useContext(GoalPreferencesContext);
